<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { OnClickOutside } from '@vueuse/components';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  pages: { type: Array, default: () => [] },
  selectedPageId: { type: String, default: null },
  settingsSelected: { type: Boolean, default: false },
  structureLocked: { type: Boolean, default: false },
  siblings: { type: Array, default: () => [] },
  currentLocale: { type: String, default: '' },
  softSwitching: { type: Boolean, default: false },
  funnelSummary: { type: Object, default: null },
  funnelLoading: { type: Boolean, default: false },
});

const emit = defineEmits([
  'select-settings',
  'select-page',
  'toggle-page',
  'reorder',
  'change-locale',
]);

const { t } = useI18n();
const dragFrom = ref(null);
const localeMenuOpen = ref(false);

const showLocaleSelect = computed(() => (props.siblings || []).length > 1);
const currentLocaleLabel = computed(() =>
  String(props.currentLocale || '').toUpperCase()
);

const pageLabel = page =>
  page.label ||
  t(`BOOKING_WIDGETS.SIDEBAR.${page.type}`) ||
  page.type;

/** Prefer visitor-facing page description (editable in General) over admin meta. */
const pageDescription = page =>
  page.description || page.sidebarDescription || '';

const CORE_PAGE_TYPES = ['filter', 'offering', 'event_information'];
const TERMINAL_PAGE_TYPES = ['review_submit', 'confirmation'];

const isPinnedPage = page =>
  page?.movable === false ||
  CORE_PAGE_TYPES.includes(page?.type) ||
  TERMINAL_PAGE_TYPES.includes(page?.type);

const canDragPage = page => !props.structureLocked && !isPinnedPage(page);

const onDragStart = (index, event) => {
  const page = props.pages[index];
  if (!canDragPage(page)) {
    event.preventDefault();
    return;
  }
  dragFrom.value = index;
  event.dataTransfer.effectAllowed = 'move';
};

const onDrop = index => {
  if (props.structureLocked) return;
  if (dragFrom.value == null || dragFrom.value === index) return;
  const fromPage = props.pages[dragFrom.value];
  if (isPinnedPage(fromPage)) {
    dragFrom.value = null;
    return;
  }
  emit('reorder', { from: dragFrom.value, to: index });
  dragFrom.value = null;
};

const pickLocale = sibling => {
  localeMenuOpen.value = false;
  if (sibling) emit('change-locale', sibling);
};

const funnelLabel = computed(() => {
  if (props.funnelLoading) return t('BOOKING_WIDGETS.FUNNEL.LOADING');
  const summary = props.funnelSummary;
  if (!summary) return t('BOOKING_WIDGETS.FUNNEL.UNAVAILABLE');
  const sessions = summary.sessions_started ?? 0;
  const completed = summary.bookings_completed ?? 0;
  const rate = summary.conversion_rate ?? 0;
  return t('BOOKING_WIDGETS.FUNNEL.SUMMARY', {
    sessions,
    completed,
    rate,
  });
});
</script>

<template>
  <aside
    class="flex h-full min-h-0 w-80 shrink-0 flex-col overflow-hidden border-r border-n-weak bg-n-solid-2"
  >
    <div
      class="flex h-14 shrink-0 items-center gap-2 border-b border-n-weak px-4"
    >
      <h2 class="min-w-0 flex-1 truncate text-lg font-semibold text-n-slate-12">
        {{ t('BOOKING_WIDGETS.WIDGET_SETTINGS') }}
      </h2>
      <OnClickOutside
        v-if="showLocaleSelect"
        @trigger="localeMenuOpen = false"
      >
        <div class="relative shrink-0">
          <button
            type="button"
            class="inline-flex h-6 items-center justify-center gap-1 rounded border border-n-weak bg-n-solid-1 px-1.5 text-[10px] font-semibold uppercase leading-none text-n-slate-12 hover:bg-n-alpha-1 disabled:opacity-60"
            :disabled="softSwitching"
            :aria-label="t('BOOKING_WIDGETS.LOCALE')"
            @click="localeMenuOpen = !localeMenuOpen"
          >
            <Icon
              icon="i-lucide-languages"
              class="size-3 shrink-0 text-n-slate-11"
            />
            {{ currentLocaleLabel }}
            <span class="i-lucide-chevron-down size-2.5 text-n-slate-11" />
          </button>
          <div
            v-if="localeMenuOpen"
            class="absolute right-0 z-20 mt-1 min-w-[3.5rem] overflow-hidden rounded-md border border-n-weak bg-n-solid-2 py-0.5 shadow-lg"
          >
            <button
              v-for="sibling in siblings"
              :key="sibling.id"
              type="button"
              class="block w-full px-2.5 py-1 text-left text-[10px] font-semibold uppercase text-n-slate-12 hover:bg-n-alpha-1"
              :class="{
                'bg-n-alpha-2':
                  String(sibling.locale) === String(currentLocale),
              }"
              @click="pickLocale(sibling)"
            >
              {{ String(sibling.locale).toUpperCase() }}
            </button>
          </div>
        </div>
      </OnClickOutside>
    </div>

    <div
      class="min-h-0 flex-1 overflow-y-auto py-2 transition-opacity duration-200"
      :class="{ 'opacity-60': softSwitching }"
    >
      <div
        v-for="(page, index) in pages"
        :key="page.id"
        class="mx-2 mb-1 flex items-start gap-2 rounded-lg px-2 py-3 pb-4 hover:bg-n-alpha-1"
        :class="[
          selectedPageId === page.id && !settingsSelected
            ? 'bg-violet-500/10'
            : '',
          canDragPage(page) ? 'cursor-grab' : 'cursor-pointer',
        ]"
        :draggable="canDragPage(page)"
        @dragstart="onDragStart(index, $event)"
        @dragover.prevent
        @drop="onDrop(index)"
        @click="emit('select-page', page.id)"
      >
        <span
          class="text-n-slate-10 w-5"
          aria-hidden="true"
        >
          <Icon 
            v-if="canDragPage(page)"
            icon="i-lucide-ellipsis-vertical" 
            class="size-5 shrink-0 text-n-slate-12 opacity-30 mt-[2px]" 
          </icon>
        </span>
        <div class="min-w-0 flex-1">
          <div class="font-semibold text-n-slate-12">
            {{ pageLabel(page) }}
          </div>
          <div class="mt-0.5 line-clamp-2 text-sm leading-tight text-n-slate-11">
            {{ pageDescription(page) }}
          </div>
        </div>
        <div @click.stop>
          <Switch
            v-if="page.disableable !== false"
            :model-value="page.enabled !== false"
            :disabled="structureLocked"
            @update:model-value="emit('toggle-page', { id: page.id, enabled: $event })"
          />
        </div>
      </div>
    </div>

    <div
      class="shrink-0 border-t border-n-weak px-4 py-2"
      :title="t('BOOKING_WIDGETS.FUNNEL.HELP')"
    >
      <p class="text-[11px] leading-snug text-n-slate-11">
        {{ funnelLabel }}
      </p>
    </div>

    <div class="shrink-0 border-t border-n-weak p-2">
      <button
        type="button"
        class="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-base !font-semibold text-n-slate-12 hover:bg-n-alpha-1"
        :class="settingsSelected ? 'bg-violet-500/10' : ''"
        @click="emit('select-settings')"
      >
        <Icon icon="i-lucide-settings" class="size-4 shrink-0 text-n-slate-12" />
        {{ t('BOOKING_WIDGETS.SETTINGS') }}
      </button>
    </div>
  </aside>
</template>
